<template>
  <Portal>
    <Modal
      class="verify-email-modal"
      data-testid="verify-email-modal"
      :is-open="isOpen"
      :show-back-button="false"
      size="md"
      title=""
      @close="handleClose"
    >
      <HelperViewContextual
        icon="ED0070"
        title="Valide seu e-mail"
        :description="descriptionComputed"
        button-label="Reenviar e-mail"
        :button-loading="isLoading"
        :button-disabled="isLoading"
        @click="handleResendEmail"
      />
    </Modal>
  </Portal>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { Portal } from '@/modules/common/components/Portal';
import { Modal, HelperViewContextual } from '@warrenbrasil/nebraska-web';
import { customerAPI } from '@/modules/common/services/http/customer';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/wallet-builder/src/types';
import { CustomerModule } from '@/modules/common/store/CustomerModule';

interface Props {
  isOpen: boolean;
}
defineProps<Props>();

interface Emits {
  (e: 'close'): void;
}
const emit = defineEmits<Emits>();

const isLoading = ref(false);
const customerEmail = ref('');

const handleClose = () => {
  emit('close');
};

const descriptionComputed = computed(
  () =>
    `Enviamos um link para o e-mail ${customerEmail.value}. Acesse-o para prosseguir com a validação.`
);

const getCustomerEmail = async () => {
  isLoading.value = true;
  try {
    customerEmail.value = CustomerModule.getCustomer?.email ?? '';
    isLoading.value = false;
  } catch (_) {
    notify({
      group: 'wrn-notifications',
      text: 'Tivemos um problema para obter seu e-mail. Tente novamente.',
      type: NotificationTypes.error
    });
    handleClose();
  }
};

const handleResendEmail = async () => {
  isLoading.value = true;
  try {
    await customerAPI.checkAccount(customerEmail.value);
    isLoading.value = false;
    notify({
      group: 'wrn-notifications',
      text: 'Pronto, enviamos um novo e-mail.',
      type: NotificationTypes.success
    });
    handleClose();
  } catch (_) {
    isLoading.value = false;
    notify({
      group: 'wrn-notifications',
      text: 'Tivemos um problema para enviar o e-mail. Tente novamente.',
      type: NotificationTypes.error
    });
    handleClose();
  }
};

onMounted(getCustomerEmail);
</script>

<style lang="less" scoped>
.verify-email-modal {
  :deep(.modal-content) {
    padding: 0 @size-spacing-x1000 @size-spacing-x1000 @size-spacing-x1000;
  }

  :deep(.initial-avatar-icon) {
    background: @status-info-background;
    color: @asset-04;
  }
}
</style>
